import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  getSession,
  maybeRecordOAuthLogin,
  onAuthStateChange,
  sessionsEqual,
  signOut,
  type Session,
} from '@pc/db';

interface AuthCtx {
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

/**
 * MUST stay in sync with the `storageKey` passed to `initSupabase()` in
 * `apps/shop/src/App.tsx`. Used by `readPersistedSession()` to do a
 * synchronous, supabase-js-independent read of the user's session at
 * AuthProvider mount.
 */
const SHOP_AUTH_STORAGE_KEY = 'pc-shop.auth';

/**
 * Hard cap on how long we'll wait for supabase-js to emit its first
 * `INITIAL_SESSION` event before falling back to a direct getSession().
 */
const SESSION_BOOTSTRAP_TIMEOUT_MS = 4000;

interface MaybePersistedSession {
  access_token?: unknown;
  refresh_token?: unknown;
  expires_at?: unknown;
}

/**
 * Synchronously read the persisted Supabase session from `localStorage`
 * at AuthProvider mount, BEFORE supabase-js has had a chance to run
 * `_initialize() -> _recoverAndRefresh() -> _emitInitialSession()`.
 *
 * Why this exists (and why the previous listener-only fix wasn't
 * enough): in production we observed that even with a valid,
 * non-expired session sitting in `localStorage['pc-shop.auth']`, the
 * shop SPA was rendering `RequireAuth`'s "no session" branch on every
 * hard refresh -- redirecting authenticated users to `/sign-in` (or in
 * the case of the `/` root, the public marketing landing page) before
 * the listener could push the recovered session into React state. The
 * first listener-based fix relied on supabase-js firing
 * `INITIAL_SESSION` quickly, but in this repo's setup that emission
 * can be delayed (or, in the worst observed cases, swallowed by an
 * unhandled internal error path) long enough that the user perceives
 * "logged in -> reload shows me logged out".
 *
 * The robust fix is to bypass supabase-js for the initial paint and
 * seed React state from the persisted blob directly. The session
 * structure supabase-js stores is stable (access_token, refresh_token,
 * expires_at, user, ...); we only need enough of it to recognize "the
 * user is authenticated" for routing. supabase-js's own listener still
 * wires up and supersedes our seed within a few hundred ms, so any
 * server-side revocation, refresh, or sign-out is reflected promptly.
 *
 * Optimistic auth is intentional here: we'd rather briefly show the
 * authenticated shell to a user whose session was revoked
 * server-side (which they'll learn about on their next RPC) than
 * briefly show the unauthenticated shell to a user who is actually
 * signed in (which they perceive as broken auth).
 */
function readPersistedSession(): Session | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(SHOP_AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as MaybePersistedSession;
    if (
      !parsed ||
      typeof parsed.access_token !== 'string' ||
      typeof parsed.refresh_token !== 'string'
    ) {
      return null;
    }
    const expiresAt =
      typeof parsed.expires_at === 'number' ? parsed.expires_at : null;
    if (expiresAt !== null && expiresAt * 1000 <= Date.now()) {
      // Token is past `expires_at`. supabase-js may still be able to
      // refresh it via the refresh_token, but we shouldn't render
      // authenticated UI on a stale access_token -- let the listener
      // path own that decision so a real refresh failure can clear
      // state cleanly.
      return null;
    }
    return parsed as unknown as Session;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Synchronous seed -- no skeleton flash, no race window in which
  // `RequireAuth` can read `{ session: null, loading: false }` for a
  // user who is actually signed in.
  const [session, setSession] = useState<Session | null>(() =>
    readPersistedSession(),
  );
  // If we found a seed session, we're already "settled" enough to
  // render the authenticated shell; supabase-js will reconcile on its
  // own time. If we didn't find one, we still wait for the listener
  // (or the 4s fallback) to confirm "no session" before
  // `RequireAuth` will redirect.
  const initiallySeeded = session !== null;
  const [loading, setLoading] = useState(!initiallySeeded);
  const settledRef = useRef(initiallySeeded);

  useEffect(() => {
    let alive = true;

    const settle = (next: Session | null) => {
      if (!alive) return;
      // Functional updater + content-equality so the no-op `SIGNED_IN`
      // that supabase-js re-emits on every tab visibility change is a
      // literal React state no-op. Without this, the new Session
      // reference would cascade through every useAuth() consumer and
      // can unmount the matched page tree (resetting any in-progress
      // form). See sessionsEqual() docs in @pc/db for the full story.
      setSession((prev) => (sessionsEqual(prev, next) ? prev : next));
      if (!settledRef.current) {
        settledRef.current = true;
        setLoading(false);
      }
    };

    const off = onAuthStateChange((event, s) => {
      // Forward every event from supabase-js (INITIAL_SESSION,
      // SIGNED_IN, SIGNED_OUT, TOKEN_REFRESHED, USER_UPDATED) into
      // state. This is what catches a real server-side revocation
      // after the optimistic seed above.
      settle(s);
      // A "Continue with Google" sign-in lands back on a shop route via
      // supabase-js's detectSessionInUrl, firing SIGNED_IN without going
      // through the signInWithPassword audit writer. maybeRecordOAuthLogin
      // self-gates on the `pc.oauth.login_pending` marker, so this is a
      // no-op for password sessions, token refreshes, and visibility
      // re-emits.
      if (event === 'SIGNED_IN') {
        void maybeRecordOAuthLogin(s);
      }
    });

    const fallbackTimer = window.setTimeout(() => {
      if (settledRef.current || !alive) return;
      getSession()
        .then((s) => settle(s))
        .catch(() => settle(null));
    }, SESSION_BOOTSTRAP_TIMEOUT_MS);

    return () => {
      alive = false;
      window.clearTimeout(fallbackTimer);
      off();
    };
  }, []);

  const value = useMemo<AuthCtx>(
    () => ({
      session,
      loading,
      signOut: async () => {
        await signOut();
        setSession(null);
      },
    }),
    [session, loading],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>.');
  return v;
}
